import React, { useState, useEffect } from 'react';
import { ArrowRight, CreditCard, Plus, Eye, EyeOff, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/auth/hooks';
import AddTerminalDialog from '../../../components/AddTerminalDialog';
import { TerminalsListSkeleton } from '../../../components/invoices/TerminalSkeleton';
import { EmptyTerminalsState } from '../../../components/invoices/EmptyTerminalsState';
import { TerminalCard } from '../../../components/invoices/TerminalCard';
import { TerminalRequestCard } from '../../../components/invoices/TerminalRequestCard';
import { TwoStepDeleteDialog } from '../../../components/invoices/TwoStepDeleteDialog';

const DEFAULT_SHOP_NUMBER = '001';

const FORM_STEPS = [
  { id: 1, title: 'שם המסוף', icon: '📝' },
  { id: 2, title: 'פרטי התחברות', icon: '🔐' },
  { id: 3, title: 'הגדרות', icon: '⚙️' }
];

export default function InvoicesImproved() {
  const { user } = useAuth();
  const [businessId, setBusinessId] = useState<string | null>(null);

  const [form, setForm] = useState({
    terminal_name: '',
    is_active: true,
    enable_invoice: true,
    pelecard_terminal_number: '',
    pelecard_user: '',
    pelecard_password: '',
    pelecard_shop_number: DEFAULT_SHOP_NUMBER,
    sandbox_mode: false,
  });

  const [loading, setLoading] = useState(false);
  const [terminals, setTerminals] = useState<any[]>([]);
  const [loadingTerminals, setLoadingTerminals] = useState(false);
  const [editTerminal, setEditTerminal] = useState<any | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [terminalRequests, setTerminalRequests] = useState<any[]>([]);
  const [continueRequest, setContinueRequest] = useState<any | null>(null);
  const [activateRequest, setActivateRequest] = useState<any | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; terminalId: string | null; step: 1 | 2 }>({
    isOpen: false,
    terminalId: null,
    step: 1
  });

  useEffect(() => {
    const fetchBusinessId = async () => {
      if (!user?.id) return;

      const { data: userData, error } = await supabase
        .from('users')
        .select('business_id')
        .eq('id', user.id)
        .single();

      if (error) {

        toast.error('שגיאה בטעינת פרטי העסק');
        return;
      }

      if (userData?.business_id) {
        setBusinessId(userData.business_id);
      }
    };

    fetchBusinessId();
  }, [user?.id]);

  const fetchTerminals = async () => {
    if (!businessId) return;
    setLoadingTerminals(true);
    const { data, error } = await supabase
      .from('payment_terminals')
      .select(`
        *,
        terminal_request:terminal_requests(form_data)
      `)
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });

    if (error) toast.error('שגיאה בטעינת מסופים');
    setTerminals(data || []);
    setLoadingTerminals(false);
  };

  useEffect(() => {
    fetchTerminals();
  }, [businessId]);

  const fetchTerminalRequests = async () => {
    if (!user?.id) return;
    const { data, error } = await supabase
      .from('terminal_requests')
      .select('*')
      .eq('user_id', user.id)
      .in('status', ['draft', 'payment_completed', 'monthly_pending', 'form_pending', 'waiting_for_shipment', 'shipped', 'ready_to_activate'])
      .order('created_at', { ascending: false });
    if (!error && data) {
      setTerminalRequests(data);
    }
  };

  useEffect(() => {
    fetchTerminalRequests();
  }, [user?.id]);

  useEffect(() => {
    if (!deleteConfirm.isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDeleteConfirm({ isOpen: false, terminalId: null, step: 1 });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deleteConfirm.isOpen]);

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};

    if (step === 0) {
      if (!form.terminal_name.trim()) {
        errors.terminal_name = 'יש להזין שם למסוף';
      }
    } else if (step === 1) {
      if (!form.pelecard_terminal_number.trim()) {
        errors.pelecard_terminal_number = 'נדרש מספר מסוף';
      }
      if (!form.pelecard_user.trim()) {
        errors.pelecard_user = 'נדרש שם משתמש';
      }
      if (!editTerminal && !form.pelecard_password.trim()) {
        errors.pelecard_password = 'נדרשת סיסמה';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {

    if (isTransitioning) {

      return;
    }
    if (validateStep(currentStep)) {

      setIsTransitioning(true);
      setCurrentStep(prev => Math.min(prev + 1, FORM_STEPS.length - 1));
      setTimeout(() => {
        setIsTransitioning(false);

      }, 500);
    } else {

    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    setFormErrors({});
  };

  const handleEdit = (terminal: any, jumpToActivation: boolean = false) => {

    setForm({
      terminal_name: terminal.terminal_name || '',
      is_active: jumpToActivation ? true : (terminal.is_active ?? true),
      enable_invoice: terminal.enable_invoice ?? true,
      pelecard_terminal_number: terminal.pelecard_terminal_number || '',
      pelecard_user: terminal.pelecard_user || '',
      pelecard_password: '',
      pelecard_shop_number: terminal.pelecard_shop_number || DEFAULT_SHOP_NUMBER,
      sandbox_mode: terminal.sandbox_mode ?? false,
    });
    setEditTerminal(terminal);
    setShowForm(true);
    setCurrentStep(0);
    setLoading(false);
  };

  const handleCancel = () => {

    setShowForm(false);
    setEditTerminal(null);
    setActivateRequest(null);
    setCurrentStep(0);
    setFormErrors({});
    setShowPassword(false);
    setLoading(false);
    setIsTransitioning(false);
    setForm({
      terminal_name: '',
      is_active: true,
      enable_invoice: true,
      pelecard_terminal_number: '',
      pelecard_user: '',
      pelecard_password: '',
      pelecard_shop_number: DEFAULT_SHOP_NUMBER,
      sandbox_mode: false,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isTransitioning) {

      return;
    }

    if (loading) {

      return;
    }

    if (!validateStep(0) || !validateStep(1)) {

      toast.error('יש למלא את כל השדות הנדרשים');
      setLoading(false);
      return;
    }

    setLoading(true);
    if (!businessId) {
      toast.error('לא נמצא מזהה עסק');
      setLoading(false);
      return;
    }

    try {

      const terminalData: any = {
        terminal_name: form.terminal_name,
        business_id: businessId,
        is_active: form.is_active,
        enable_invoice: form.enable_invoice,
        pelecard_terminal_number: form.pelecard_terminal_number,
        pelecard_user: form.pelecard_user,
        pelecard_shop_number: form.pelecard_shop_number,
        sandbox_mode: form.sandbox_mode,
      };

      if (editTerminal && !form.pelecard_password) {
        delete terminalData.pelecard_password;
      } else if (form.pelecard_password) {
        try {
          const response = await fetch('https://nkuqcyelxgyihrxyvitb.supabase.co/functions/v1/encrypt-password', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || ''}`
            },
            body: JSON.stringify({ plaintext: form.pelecard_password }),
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.error || 'שגיאה בהצפנת סיסמה');
          }

          terminalData.pelecard_password = result.encrypted;
        } catch (encryptError: any) {

          throw new Error('שגיאה בהצפנת סיסמה: ' + encryptError.message);
        }
      }

      if (editTerminal) {
        terminalData.id = editTerminal.id;
      }

      if (activateRequest) {
        terminalData.terminal_request_id = activateRequest.id;
      }

      const { data: insertedTerminal, error } = await supabase.from('payment_terminals').upsert([terminalData]).select().single();

      if (error) {

        throw new Error(error.message);
      }

      const { data: verifyTerminal, error: verifyError } = await supabase
        .from('payment_terminals')
        .select('*')
        .eq('id', insertedTerminal.id)
        .single();

      if (verifyError) {

      } else {

        if (!verifyTerminal.is_active) {

          toast.error('המסוף נשמר אך לא הופעל. אנא בדוק את ההגדרות.');
        }
      }

      if (activateRequest) {
        await supabase
          .from('terminal_requests')
          .update({
            status: 'activated',
            activated_at: new Date().toISOString()
          })
          .eq('id', activateRequest.id);
        setActivateRequest(null);
        fetchTerminalRequests();
      }

      await fetchTerminals();
      toast.success(editTerminal ? 'המסוף עודכן בהצלחה' : activateRequest ? 'המסוף הופעל בהצלחה!' : 'המסוף נוסף בהצלחה');
      handleCancel();
    } catch (error: any) {

      toast.error('שגיאה: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (terminalId: string) => {
    setDeleteConfirm({ isOpen: true, terminalId, step: 1 });
  };

  const handleDeleteStep1Continue = () => {
    setDeleteConfirm(prev => ({ ...prev, step: 2 }));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.terminalId) return;

    try {
      // מצא את המסוף שנמחק
      const deletedTerminal = terminals.find(t => t.id === deleteConfirm.terminalId);

      if (!deletedTerminal) {
        throw new Error('המסוף לא נמצא');
      }

      // מחק את המסוף מהטבלה
      const { error: deleteError } = await supabase
        .from('payment_terminals')
        .delete()
        .eq('id', deleteConfirm.terminalId);

      if (deleteError) throw deleteError;

      // תעד את המחיקה ב-terminal_requests
      const deletionRecord = {
        business_id: businessId,
        user_id: user?.id,
        terminal_model: 'DELETED_TERMINAL',
        status: 'deleted',
        deleted_at: new Date().toISOString(),
        deletion_reason: 'user_initiated',
        deleted_terminal_data: {
          terminal_id: deletedTerminal.id,
          terminal_name: deletedTerminal.terminal_name,
          pelecard_terminal_number: deletedTerminal.pelecard_terminal_number,
          pelecard_shop_number: deletedTerminal.pelecard_shop_number,
          pelecard_user: deletedTerminal.pelecard_user,
          enable_invoice: deletedTerminal.enable_invoice,
          is_active: deletedTerminal.is_active,
          sandbox_mode: deletedTerminal.sandbox_mode,
          deleted_timestamp: new Date().toISOString()
        },
        form_data: {
          action: 'terminal_deletion',
          terminal_details: deletedTerminal,
          deleted_by_user: user?.id,
          deleted_at: new Date().toISOString()
        }
      };

      const { error: recordError } = await supabase
        .from('terminal_requests')
        .insert([deletionRecord]);

      if (recordError) {

      }

      // שלח webhook ל-Pelecard על המחיקה
      try {
        const webhookUrl = 'https://ofekperetz.app.n8n.cloud/webhook/06ebebce-00db-45de-bd0a-2357563517d5';
        const webhookPayload = {
          action: 'terminal_deletion',
          terminalId: deletedTerminal.id,
          terminalName: deletedTerminal.terminal_name,
          pelecardTerminalNumber: deletedTerminal.pelecard_terminal_number,
          pelecardShopNumber: deletedTerminal.pelecard_shop_number,
          pelecardUser: deletedTerminal.pelecard_user,
          businessId: businessId,
          userId: user?.id,
          deletedAt: new Date().toISOString(),
          reason: 'user_initiated_deletion'
        };

        await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(webhookPayload)
        });

      } catch (webhookError) {

      }

      toast.success('המסוף נמחק בהצלחה והעדכון נשלח ל-Pelecard');
      setTerminals(prev => prev.filter(t => t.id !== deleteConfirm.terminalId));
      setDeleteConfirm({ isOpen: false, terminalId: null, step: 1 });
    } catch (error: any) {

      toast.error('שגיאה במחיקת המסוף: ' + (error.message || 'שגיאה לא ידועה'));
      setDeleteConfirm({ isOpen: false, terminalId: null, step: 1 });
    }
  };

  const Toggle = ({ checked, onChange, label, name, disabled = false }: {
    checked: boolean;
    onChange: any;
    label: string;
    name: string;
    disabled?: boolean;
  }) => (
    <label className={`flex items-center justify-between py-3 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <span className="text-[15px] font-medium text-gray-900">{label}</span>
      <motion.div
        whileTap={{ scale: 0.95 }}
        className={`relative inline-block w-[51px] h-[31px] transition-all duration-300 rounded-full ${
          checked ? 'bg-blue-500 shadow-lg shadow-blue-200' : 'bg-gray-300'
        }`}
      >
        <input
          type="checkbox"
          name={name}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="sr-only"
        />
        <motion.span
          animate={{ x: checked ? 22 : 2 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
          className="absolute top-[2px] left-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-lg"
        />
      </motion.div>
    </label>
  );

  const handleNewTerminalRequest = (request: any) => {
    setTerminalRequests(prev => [
      ...prev,
      { ...request, status: 'pending', id: Date.now() }
    ]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/settings" className="p-2 -mr-2 hover:bg-gray-50 rounded-xl transition-colors active:scale-95">
            <ArrowRight className="h-6 w-6 text-blue-600" />
          </Link>
          <h1 className="text-[22px] font-bold text-gray-900">סליקה וחשבוניות</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4 pb-24">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-4 border border-blue-200"
        >
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                <Info className="h-5 w-5 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-[16px] font-bold text-blue-900 mb-1">מסוף סליקה</h3>
              <p className="text-[14px] text-blue-800 leading-relaxed">
                קבל תשלומים בכרטיס אשראי והפק חשבוניות אוטומטיות דרך פלאכארד בענן
              </p>
            </div>
          </div>
        </motion.div>

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-200">
                  <CreditCard className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-[18px] font-bold text-gray-900">המסופים שלי</h2>
                  <p className="text-[13px] text-gray-500">
                    {terminals.length === 0 ? 'טרם הוגדרו מסופים' : `${terminals.length} מסופים`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {loadingTerminals ? (
            <TerminalsListSkeleton />
          ) : terminals.length === 0 && !showForm ? (
            <EmptyTerminalsState />
          ) : (
            <div className="divide-y divide-gray-100">
              {terminals.map((terminal, index) => (
                <TerminalCard
                  key={terminal.id}
                  terminal={terminal}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.4 }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200"
            >
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 border-b border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[18px] font-bold text-gray-900">
                    {editTerminal ? 'עריכת מסוף' : 'הוספת מסוף חדש'}
                  </h3>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCancel}
                    className="text-[15px] text-blue-600 font-semibold hover:text-blue-700"
                  >
                    ביטול
                  </motion.button>
                </div>

                <div className="flex gap-2">
                  {FORM_STEPS.map((step, index) => (
                    <div
                      key={step.id}
                      className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                        index <= currentStep
                          ? 'bg-gradient-to-r from-blue-500 to-blue-600'
                          : 'bg-blue-200'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <form onSubmit={handleSubmit} onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
                  e.preventDefault();

                }
              }} className="p-4 space-y-6">
                <AnimatePresence mode="wait">
                  {currentStep === 0 && (
                    <motion.div
                      key="step-0"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <div className="text-center mb-4">
                        <div className="text-4xl mb-2">{FORM_STEPS[0].icon}</div>
                        <h4 className="text-[17px] font-bold text-gray-900">{FORM_STEPS[0].title}</h4>
                        <p className="text-[14px] text-gray-600">מידע על העסק והמסוף</p>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-[14px] font-semibold text-gray-700 mb-2">שם המסוף (לזיהוי)</label>
                          <input
                            type="text"
                            name="terminal_name"
                            value={form.terminal_name}
                            onChange={handleChange}
                            placeholder="מסוף קופה ראשית"
                            className={`w-full px-4 py-3.5 bg-gray-50 border-2 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                              formErrors.terminal_name ? 'border-red-500' : 'border-gray-200'
                            }`}
                            autoComplete="off"
                          />
                          {formErrors.terminal_name && (
                            <motion.p
                              initial={{ opacity: 0, y: -5 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-[13px] text-red-600 mt-1.5 font-medium"
                            >
                              {formErrors.terminal_name}
                            </motion.p>
                          )}
                        </div>
                        {editTerminal?.terminal_request?.form_data?.regId && (
                          <div>
                            <label className="block text-[14px] font-semibold text-gray-700 mb-2">ת"ז / ח"פ של העוסק</label>
                            <input
                              type="text"
                              value={editTerminal.terminal_request.form_data.regId}
                              disabled
                              className="w-full px-4 py-3.5 bg-gray-100 border-2 border-gray-200 rounded-xl text-[15px] text-gray-600 cursor-not-allowed"
                            />
                            <p className="text-[12px] text-gray-500 mt-1.5">שדה זה אינו ניתן לעריכה</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {currentStep === 1 && (
                    <motion.div
                      key="step-1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <div className="text-center mb-4">
                        <div className="text-4xl mb-2">{FORM_STEPS[1].icon}</div>
                        <h4 className="text-[17px] font-bold text-gray-900">{FORM_STEPS[1].title}</h4>
                        <p className="text-[14px] text-gray-600">הזן את הפרטים שקיבלת מפלאכארד</p>
                      </div>

                      <div>
                        <label className="block text-[14px] font-semibold text-gray-700 mb-2">מספר מסוף</label>
                        <input
                          type="text"
                          name="pelecard_terminal_number"
                          value={form.pelecard_terminal_number}
                          onChange={handleChange}
                          placeholder="12345678"
                          className={`w-full px-4 py-3.5 bg-gray-50 border-2 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                            formErrors.pelecard_terminal_number ? 'border-red-500' : 'border-gray-200'
                          }`}
                          autoComplete="off"
                        />
                        {formErrors.pelecard_terminal_number && (
                          <motion.p
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-[13px] text-red-600 mt-1.5 font-medium"
                          >
                            {formErrors.pelecard_terminal_number}
                          </motion.p>
                        )}
                      </div>

                      <div>
                        <label className="block text-[14px] font-semibold text-gray-700 mb-2">שם משתמש</label>
                        <input
                          type="text"
                          name="pelecard_user"
                          value={form.pelecard_user}
                          onChange={handleChange}
                          placeholder="username"
                          className={`w-full px-4 py-3.5 bg-gray-50 border-2 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                            formErrors.pelecard_user ? 'border-red-500' : 'border-gray-200'
                          }`}
                          autoComplete="off"
                        />
                        {formErrors.pelecard_user && (
                          <motion.p
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-[13px] text-red-600 mt-1.5 font-medium"
                          >
                            {formErrors.pelecard_user}
                          </motion.p>
                        )}
                      </div>

                      <div>
                        <label className="block text-[14px] font-semibold text-gray-700 mb-2">
                          סיסמה
                          {editTerminal && (
                            <span className="text-[12px] font-normal text-gray-500 mr-2">
                              (השאר ריק לשמירת הסיסמה הקיימת)
                            </span>
                          )}
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="pelecard_password"
                            value={form.pelecard_password}
                            onChange={handleChange}
                            placeholder="••••••••"
                            className={`w-full px-4 py-3.5 pr-12 bg-gray-50 border-2 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                              formErrors.pelecard_password ? 'border-red-500' : 'border-gray-200'
                            }`}
                            autoComplete="off"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute left-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                        {formErrors.pelecard_password && (
                          <motion.p
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-[13px] text-red-600 mt-1.5 font-medium"
                          >
                            {formErrors.pelecard_password}
                          </motion.p>
                        )}
                      </div>

                      <div>
                        <label className="block text-[14px] font-semibold text-gray-700 mb-2">מספר חנות</label>
                        <input
                          type="text"
                          name="pelecard_shop_number"
                          value={form.pelecard_shop_number}
                          onChange={handleChange}
                          placeholder="001"
                          className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                          autoComplete="off"
                        />
                        <p className="text-[13px] text-gray-500 mt-1.5">בדרך כלל מספר החנות הוא 001</p>
                      </div>
                    </motion.div>
                  )}

                  {currentStep === 2 && (
                    <motion.div
                      key="step-2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <div className="text-center mb-4">
                        <div className="text-4xl mb-2">{FORM_STEPS[2].icon}</div>
                        <h4 className="text-[17px] font-bold text-gray-900">{FORM_STEPS[2].title}</h4>
                        <p className="text-[14px] text-gray-600">בחר הגדרות המסוף</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                        <Toggle
                          checked={form.is_active}
                          onChange={handleChange}
                          label="מסוף פעיל"
                          name="is_active"
                        />
                        <div className="border-t border-gray-200"></div>
                        <Toggle
                          checked={form.sandbox_mode}
                          onChange={handleChange}
                          label="מצב פיתוח (טסט)"
                          name="sandbox_mode"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex gap-3 pt-4">
                  {currentStep > 0 && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={handlePrevStep}
                      className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold text-[16px] hover:bg-gray-200 transition-colors"
                    >
                      חזור
                    </motion.button>
                  )}
                  {currentStep < FORM_STEPS.length - 1 ? (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={handleNextStep}
                      className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold text-[16px] shadow-lg shadow-blue-200 hover:shadow-xl transition-all"
                    >
                      המשך
                    </motion.button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={loading}
                      className="flex-1 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-bold text-[16px] shadow-lg shadow-green-200 hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          שומר...
                        </>
                      ) : (
                        <>
                          {editTerminal ? 'עדכן מסוף' : 'הוסף מסוף'}
                        </>
                      )}
                    </motion.button>
                  )}
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {terminalRequests.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm overflow-hidden border-2 border-amber-200"
          >
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-4 border-b border-amber-200">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 bg-amber-600 rounded-full animate-pulse"></div>
                <h3 className="text-[18px] font-bold text-gray-900">הזמנות בתהליך</h3>
              </div>
              <p className="text-[13px] text-gray-600">המשך מאיפה שעצרת</p>
            </div>
            <div className="p-4 space-y-3">
              {terminalRequests.map((req, index) => (
                <TerminalRequestCard
                  key={req.id}
                  request={req}
                  onContinue={(request) => setContinueRequest({ ...request, _trigger: Date.now() })}
                  onActivate={async (request) => {
                    try {
                      const terminalName = request.form_data?.contactName || request.terminal_model || 'מסוף חדש';

                      const terminalData = {
                        business_id: businessId,
                        terminal_name: terminalName,
                        is_active: false,
                        enable_invoice: true,
                        terminal_request_id: request.id,
                        pelecard_terminal_number: '',
                        pelecard_user: '',
                        pelecard_password: '',
                        pelecard_shop_number: '001',
                        sandbox_mode: false,
                      };

                      const { data: insertedTerminal, error } = await supabase
                        .from('payment_terminals')
                        .insert([terminalData])
                        .select()
                        .single();

                      if (error) throw error;

                      await supabase
                        .from('terminal_requests')
                        .update({
                          status: 'activated',
                          activated_at: new Date().toISOString()
                        })
                        .eq('id', request.id);

                      toast.success('המסוף נוסף בהצלחה! עבור להגדרות כדי למלא פרטי חיבור');
                      await fetchTerminalRequests();
                      await fetchTerminals();
                    } catch (error: any) {
                      toast.error('שגיאה בהוספת המסוף: ' + error.message);
                    }
                  }}
                  index={index}
                />
              ))}
            </div>
          </motion.div>
        )}

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4">
            <AddTerminalDialog
              onNewRequest={handleNewTerminalRequest}
              continueRequest={continueRequest}
              onContinueComplete={() => {
                setContinueRequest(null);
                fetchTerminalRequests();
              }}
              hasActiveTerminal={terminals.length > 0}
              hasPendingRequest={terminalRequests.length > 0}
            />
          </div>
        </div>
      </div>

      <TwoStepDeleteDialog
        isOpen={deleteConfirm.isOpen}
        step={deleteConfirm.step}
        onStep1Continue={handleDeleteStep1Continue}
        onFinalConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, terminalId: null, step: 1 })}
      />
    </div>
  );
}
